// Slide 5 - Lextria
import { ExternalLink, Zap } from "lucide-react";

export default function Slide5() {
  const images = [
    "/lextria/lextria_1.png",
    "/lextria/lextria_2.png",
    "/lextria/lextria_3.png",
  ];

  // keywords to highlight in content
  const keywords = [
    "legal",
    "IP services",
    "clear information architecture",
    "readable content",
    "lightweight CMS",
    "contact forms",
    "inbox",
  ];

  const renderHighlighted = (text) => {
    if (!text) return null;
    const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const splitRegex = new RegExp(
      `(${keywords.map((k) => esc(k)).join("|")})`,
      "gi",
    );
    const parts = text.split(splitRegex);
    return parts.map((part, i) => {
      if (!part) return null;
      const isKeyword = keywords.some(
        (k) => k.toLowerCase() === part.toLowerCase(),
      );
      return isKeyword ? (
        <span key={i} className="keyword">
          {part}
        </span>
      ) : (
        <span key={i}>{part}</span>
      );
    });
  };

  return (
    <section className="w-full max-w-[1400px] relative z-10">
      {/* Decorative accent blob and subtle vignette to match screenshots */}
      <div className="absolute -left-24 -top-20 w-80 h-80 rounded-full bg-gradient-to-br from-slate-700/30 to-slate-900/10 blur-3xl pointer-events-none opacity-60 hidden md:block" />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 md:gap-8 lg:gap-10 items-start">
        {/* Left: Content (glass card like Slide3) */}
        <div className="space-y-2 sm:space-y-3 md:space-y-4 lg:space-y-5 p-3 sm:p-4 md:p-6 lg:p-8 xl:p-10 rounded-2xl sm:rounded-3xl bg-gradient-to-br from-white/6 via-white/3 to-transparent backdrop-blur-xl border border-white/12 shadow-2xl relative overflow-hidden transition-all duration-500">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent opacity-0 transition-opacity duration-300 pointer-events-none" />

          <div className="relative z-10">
            <div className="flex items-center gap-2 sm:gap-3">
              <span className="inline-flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 md:px-4 py-1 sm:py-1.5 md:py-2 bg-gradient-to-r from-white/10 to-white/4 rounded-full text-white text-xs sm:text-sm uppercase tracking-wider font-bold border border-white/20 shadow-lg">
                <Zap
                  size={10}
                  className="opacity-80 sm:w-3 sm:h-3 md:w-3.5 md:h-3.5"
                />
                Website
              </span>
            </div>

            <div className="mt-2 sm:mt-3 md:mt-4 lg:mt-6">
              <h2
                className="text-white font-black leading-none tracking-tight drop-shadow-lg"
                style={{ fontSize: "clamp(32px,6vw,60px)" }}
              >
                Lextria
              </h2>
              <div className="inline-block px-2 sm:px-3 py-0.5 sm:py-1 bg-white/10 rounded-lg mt-1 sm:mt-2">
                <span className="text-white/70 uppercase tracking-widest text-[10px] sm:text-xs md:text-sm font-bold">
                  Legal firm website
                </span>
              </div>
            </div>

            <p className="text-white/90 text-sm sm:text-base md:text-base lg:text-lg leading-5 sm:leading-6 md:leading-7 mt-2 sm:mt-3 md:mt-4 lg:mt-6 max-w-[560px]">
              {renderHighlighted(
                "Clean, professional site for legal and IP services with a focus on clear information architecture, readable content, and a lightweight CMS — contact forms route to your inbox for fast responses.",
              )}
            </p>

            <div className="h-px bg-white/6 my-2 sm:my-3 md:my-4 lg:my-6" />

            <div className="flex items-center gap-2 sm:gap-3">
              <div className="text-white/70 text-xs sm:text-sm md:text-sm font-semibold">
                Delivered in
              </div>
              <span className="relative flex items-center">
                <span className="absolute inline-flex h-2.5 w-2.5 sm:h-3 sm:w-3 md:h-3.5 md:w-3.5 rounded-full bg-white/60 opacity-75 animate-ping" />
                <span className="relative inline-block h-1.5 w-1.5 sm:h-2 sm:w-2 md:h-2.5 md:w-2.5 rounded-full bg-white shadow" />
              </span>
              <div className="text-white font-medium text-sm sm:text-base md:text-base">
                21 days
              </div>
            </div>

            <div className="mt-2 sm:mt-3 md:mt-4 lg:mt-6">
              <a
                href="https://lextria.in"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 sm:gap-2 bg-white text-gray-900 px-3 sm:px-4 md:px-5 lg:px-6 py-1.5 sm:py-2 md:py-2.5 rounded-full font-semibold text-sm sm:text-base shadow-md transition-transform hover:scale-105 border border-white/20"
              >
                Visit Website
                <ExternalLink
                  size={12}
                  className="sm:w-3 sm:h-3 md:w-4 md:h-4"
                />
              </a>
            </div>
          </div>
        </div>

        {/* Right: Enhanced Gallery (hero + two thumbs like Slide3 but one large + two smaller) */}
        <div className="relative">
          <div className="absolute -inset-4 bg-gradient-to-br from-white/6 via-transparent to-white/4 rounded-3xl blur-2xl opacity-40 pointer-events-none" />

          <div className="relative grid grid-cols-2 gap-1.5 sm:gap-2 md:gap-2.5">
            <div className="col-span-2">
              <figure className="m-0 bg-white/12 p-2 sm:p-3 md:p-4 rounded-2xl sm:rounded-3xl border border-white/20 shadow-2xl overflow-hidden relative">
                <div className="absolute inset-0 bg-gradient-to-t from-black/24 via-transparent to-transparent opacity-0 transition-opacity duration-500 z-10" />
                <img
                  src={images[0]}
                  alt="Lextria hero"
                  className="w-full rounded-lg object-contain"
                  style={{ aspectRatio: "16/10" }}
                />
              </figure>
            </div>

            {images.slice(1).map((img, idx) => (
              <figure
                key={idx}
                className="bg-white/12 p-1.5 sm:p-2 rounded-xl sm:rounded-2xl border border-white/20 shadow-lg overflow-hidden relative"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-white/0 to-white/12 opacity-0 transition-opacity duration-300 z-10" />
                <img
                  src={img}
                  alt={`Lextria detail ${idx + 1}`}
                  className="w-full rounded-lg object-contain"
                  style={{ aspectRatio: "16/9" }}
                />
              </figure>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
